import { useLayoutEffect, useState } from "react";

import { useAppActive } from "./useAppActive";
import {
  nextDisplayChangeAt,
  readClock,
  useClockOf,
  useWakeAt,
  type TimerClock,
} from "./timerClock";

export type TimerDisplay = {
  /** The number the readout shows: remaining down, counted up, the overshoot
   *  of an overtime countdown. */
  displaySec: number;
  /** Past an overtime countdown's target: the caller prefixes a +. */
  isOvertime: boolean;
};

// The seconds of a running count, for the ONE component that shows them
// (#326). The engine above it no longer re-renders with time: it wakes only for
// the 3-2-1 and zero, so the whole logger stays still while a rest runs, and
// only the text holding the number re-renders, once per shown second.
//
// The wake is a single timeout aimed at the NEXT change of the shown second,
// not an interval: one render per second, on the second the wall clock says,
// never a 250ms poll that lands a quarter-second late. Paused, idle, or a
// countdown sitting at zero: no timeout at all. In background nothing is
// armed (#325), and the clock is re-read on the way back.
//
// Not useNowTick: that one beats every second from whenever it started, which
// drifts against a deadline set at an arbitrary millisecond.
export function useTimerDisplay(clock: TimerClock): TimerDisplay {
  const c = useClockOf(clock);
  const active = useAppActive();
  const [now, setNow] = useState(() => Date.now());

  // The count itself changed (start, ±N, pause, resume) or the app came back:
  // read the wall clock BEFORE paint, so the old reading never flashes against
  // the new deadline.
  useLayoutEffect(() => {
    if (active) setNow(Date.now());
  }, [active, c]);

  useWakeAt(active ? nextDisplayChangeAt(c, now) : null, setNow);
  const { displaySec, isOvertime } = readClock(c, now);
  return { displaySec, isOvertime };
}
